<template>
    <div class="page_wrap">
        <div class="top-card">
            <div class="nav_title">默认角色</div>
            <div class="content-box">
                <!-- 角色卡片列表 -->
                <div class="role-cards">
                    <div class="role-card" v-for="(role, index) in roleList" :key="index">
                        <div class="role-header">
                            <div class="role-name">{{ role.role_name }}</div>
                        </div>
                        <div class="role-desc">
                            <span class="desc-label">角色描述：</span>
                            <div class="desc-content">{{ role.role_desc }}</div>
                        </div>
                        <div class="role-members">
                            <span class="members-label">角色成员：</span>
                            <div class="members-list">
                                <template v-for="(member, idx) in role.members">
                                    <Tag 
                                        :key="idx" 
                                        closable
                                        @on-close="removeMember(role, member)"
                                        v-if="role.can_edit"
                                    >
                                        {{ member.real_name || member.name }}
                                    </Tag>
                                    <Tag :key="'no-close-' + idx" v-else>
                                        {{ member.real_name || member.name }}
                                    </Tag>
                                </template>
                                <div class="add-member-wrapper" v-if="role.can_edit">
                                    <div
                                        class="add-member-btn"
                                        :class="{ open: showDropdownIndex === index }"
                                        @click.stop="showMemberDropdown(role, index, $event)"
                                    >
                                        <Icon type="md-add" size="20" />
                                    </div>
                                    <!-- 添加成员下拉菜单 -->
                                    <div 
                                        class="member-dropdown" 
                                        v-if="showDropdownIndex === index"
                                        v-click-outside="hideDropdown"
                                        @click.stop
                                    >
                                        <div class="search-box">
                                            <Input 
                                                v-model="searchKeyword" 
                                                placeholder="搜索员工名称" 
                                                @on-input="searchEmployees"
                                                clearable
                                            />
                                        </div>
                                        <div class="employee-list" @scroll="handleEmployeeScroll">
                                            <div 
                                                class="employee-item" 
                                                v-for="(emp, empIdx) in filteredEmployees" 
                                                :key="empIdx"
                                                @click="addMemberToRole(role, emp)"
                                            >
                                                <span class="employee-name">{{ emp.real_name || emp.name }}</span>
                                                <span class="employee-phone">{{ emp.mobile }}</span>
                                                <span class="check-slot">
                                                    <Icon 
                                                        type="md-checkmark" 
                                                        size="20" 
                                                        class="check-icon"
                                                        :style="{ visibility: isMemberSelected(role, emp) ? 'visible' : 'hidden' }"
                                                    />
                                                </span>
                                            </div>
                                            <div v-if="filteredEmployees.length === 0" class="no-data">
                                                暂无数据
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <span
                                v-if="canShowEditRole(role)"
                                class="edit-link edit-link--members"
                                style="color: #4877E8;font-size: 14px;cursor: pointer;text-decoration: none;transition: color 0.2s;"
                                @click="editRole(role)"
                            >修改</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- 设置法定代表人弹窗 -->
        <Modal
            v-model="legalRepModalVisible"
            title="设置法定代表人"
            width="760"
            class-name="legal-rep-modal"
            :mask-closable="false"
        >
            <div class="legal-rep-modal__body">
                <div class="legal-rep-modal__search">
                    <span class="legal-rep-modal__tip">请选择法定代表人</span>
                </div>

                <div class="legal-rep-modal__list">
                    <div
                        class="legal-rep-row"
                        v-for="emp in legalRepEmployees"
                        :key="emp.id"
                        @click="selectLegalRep(emp)"
                    >
                        <div class="legal-rep-row__left">
                            <Checkbox
                                :value="emp.id === legalRepSelectedId"
                                @on-change="onLegalRepCheck(emp, $event)"
                            />
                            <span class="legal-rep-row__name">
                                {{ (emp.real_name || emp.name) + (emp.is_legal_person ? '（已认证）' : '') }}
                            </span>
                        </div>
                        <div class="legal-rep-row__right">{{ emp.mobile }}</div>
                    </div>
                    <div v-if="legalRepEmployees.length === 0" class="legal-rep-modal__empty">
                        暂无数据
                    </div>
                </div>
            </div>

            <div slot="footer">
                <Button @click="closeLegalRepModal">取消</Button>
                <Button type="primary" @click="confirmLegalRep">确认</Button>
            </div>
        </Modal>
    </div>
</template>

<script>
import { get_enterprise_info, getMemberList, getRoleUserlist, upMemberPower, getCompanyLegalperson } from '@/api/interFace'
export default {
    name: 'enterprise-role',
    directives: {
        'click-outside': {
            bind(el, binding) {
                el.clickOutsideEvent = function(event) {
                    if (!(el === event.target || el.contains(event.target))) {
                        binding.value();
                    }
                };
                document.addEventListener('click', el.clickOutsideEvent);
            },
            unbind(el) {
                document.removeEventListener('click', el.clickOutsideEvent);
            }
        }
    },
    data() {
        return {
            roleList: [],
            showDropdownIndex: -1,
            searchKeyword: '',
            allEmployees: [], // 所有员工列表
            filteredEmployees: [], // 过滤后的员工列表
            currentDropdownRole: null,
            enterpriseAuthId: '',
            employeePage: 1,
            employeeLastPage: 1,
            employeeLoading: false,

            legalRepModalVisible: false,
            legalRepAllEmployees: [],
            legalRepEmployees: [],
            legalRepSelectedId: null,
            legalRepRoleRef: null,
            legalRepPage: 1,
            legalRepLastPage: 1,
            legalRepLoading: false,
        }
    },
    created() {
        this.init()
    },
    methods: {
        async init() {
            await this.getEnterpriseAuthId()
            await this.refreshRoleUsers()
        },
        async getEnterpriseAuthId() {
            try {
                const res = await get_enterprise_info({ enterprise_auth_id: this.enterpriseAuthId || localStorage.getItem('enterprise_auth_id') || '' })
                if (res && res.code === 200 && res.data && res.data.enterprise_info) {
                    this.enterpriseAuthId = res.data.enterprise_info.id
                } else {
                    this.enterpriseAuthId = ''
                }
            } catch (e) {
                this.enterpriseAuthId = ''
            }
        },
        canShowEditRole(role) {
            return role && role.role_name === '法定代表人' 
        },
        parseEmployeesResponse(res) {
            const userList = (((res || {}).data || {}).user_list) || {}
            const list = userList.data || []
            return {
                current_page: Number(userList.current_page || 1),
                last_page: Number(userList.last_page || 1),
                list: list.map(u => ({
                    id: u.id,
                    real_name: u.real_name,
                    mobile: u.phone
                }))
            }
        },
        async refreshRoleUsers() {
            if (!this.enterpriseAuthId) {
                this.roleList = []
                return
            }
            const res = await getRoleUserlist({ enterprise_auth_id: this.enterpriseAuthId })
            const data = (res && res.data) || {}
            const normalizeMembers = (arr) => (arr || []).map(u => ({
                id: u.id,
                real_name: u.real_name,
                mobile: u.phone
            }))
            this.roleList = [
                {
                    role_key: 'legal',
                    role_name: '法定代表人',
                    role_desc: '企业法定代表人(个体工商户的经营者),在系统中拥有所有的查看及管理权限',
                    members: normalizeMembers(data.legal_persons),
                    can_edit: false,
                    power_type: 1
                },
                {
                    role_key: 'seal',
                    role_name: '印章管理员',
                    role_desc: '对成员权限、企业信息进行配置',
                    members: normalizeMembers(data.seal_users),
                    can_edit: true,
                    power_type: 2
                },
                {
                    role_key: 'template',
                    role_name: '模板管理员',
                    role_desc: '创建新的文件模板,管理所有文件模板',
                    members: normalizeMembers(data.template_users),
                    can_edit: true,
                    power_type: 3
                },
                {
                    role_key: 'evidence',
                    role_name: '证据管理员',
                    role_desc: '创建新的文件模板,管理所有文件模板，管理存证数据，包括查看、下载存证数据',
                    members: normalizeMembers(data.evidence_users),
                    can_edit: true,
                    power_type: 4
                }
            ]
        },
        async loadEmployeesPage({ page = 1, keyword = '', append = false } = {}) {
            if (this.employeeLoading) return
            this.employeeLoading = true
            try {
                const res = await getMemberList({
                    status: 1,
                    page,
                    limit: 10,
                    name: keyword,
                    enterprise_auth_id: this.enterpriseAuthId
                })
                const parsed = this.parseEmployeesResponse(res)
                this.employeePage = parsed.current_page
                this.employeeLastPage = parsed.last_page
                if (append) {
                    // 追加时避免重复
                    const existing = new Set((this.allEmployees || []).map(e => e.id))
                    const next = parsed.list.filter(e => !existing.has(e.id))
                    this.allEmployees = [...this.allEmployees, ...next]
                } else {
                    this.allEmployees = parsed.list
                }
                this.filteredEmployees = this.allEmployees
            } finally {
                this.employeeLoading = false
            }
        },

        async loadLegalRepList() {
            if (!this.enterpriseAuthId) {
                this.legalRepAllEmployees = []
                this.legalRepEmployees = []
                return
            }
            this.legalRepLoading = true
            try {
                const res = await getCompanyLegalperson({ enterprise_auth_id: this.enterpriseAuthId })
                const list = (res && res.data) || []
                this.legalRepAllEmployees = list.map(u => ({
                    id: u.id,
                    real_name: u.real_name,
                    mobile: u.phone,
                    is_legal_person: u.is_legal_person
                }))
                this.legalRepEmployees = this.legalRepAllEmployees
            } finally {
                this.legalRepLoading = false
            }
        },
        // 显示成员下拉菜单
        async showMemberDropdown(role, index, e) {
            if (e && typeof e.stopPropagation === 'function') e.stopPropagation()
            if (this.showDropdownIndex === index) {
                this.showDropdownIndex = -1
            } else {
                this.showDropdownIndex = index
                this.searchKeyword = ''
                this.currentDropdownRole = role
                this.employeePage = 1
                this.employeeLastPage = 1
                await this.loadEmployeesPage({ page: 1, keyword: '', append: false })
            }
        },
        // 隐藏下拉菜单
        hideDropdown() {
            this.showDropdownIndex = -1
            this.searchKeyword = ''
            this.currentDropdownRole = null
            this.employeePage = 1
            this.employeeLastPage = 1
        },
        // 搜索员工
        async searchEmployees() {
            try {
                const kw = (this.searchKeyword || '').trim()
                if (!kw) {
                    this.employeePage = 1
                    this.employeeLastPage = 1
                    await this.loadEmployeesPage({ page: 1, keyword: '', append: false })
                    return
                }
                // 搜索时从第一页开始
                this.employeePage = 1
                this.employeeLastPage = 1
                await this.loadEmployeesPage({ page: 1, keyword: kw, append: false })
            } catch (e) {
                this.filteredEmployees = []
            }
        },
        // 下拉滚动到底部自动加载下一页
        async handleEmployeeScroll(e) {
            const el = e && e.target
            if (!el) return
            const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 16
            if (!nearBottom) return
            if (this.employeeLoading) return
            if (this.employeePage >= this.employeeLastPage) return
            const kw = (this.searchKeyword || '').trim()
            await this.loadEmployeesPage({ page: this.employeePage + 1, keyword: kw, append: true })
        },
        // 判断员工是否已在该角色中
        isMemberSelected(role, employee) {
            return role.members.some(member => member.id === employee.id)
        },
        // 添加成员到角色
        async addMemberToRole(role, employee) {
            if (!role || !employee) return
            const exists = role.members.some(m => m.id === employee.id)
            const nextStatus = exists ? 0 : 1
            try {
                await upMemberPower({
                    member_id: employee.id,
                    type: role.power_type,
                    status: nextStatus
                })
                this.$Message.success(nextStatus === 1 ? '已添加成员' : '已移除成员')
                await this.refreshRoleUsers()
            } catch (err) {
                this.$Message.error(err.msg || '操作失败')
            }
        },
        // 移除成员
        async removeMember(role, member) {
            if (!role || !member) return
            try {
                await upMemberPower({
                    member_id: member.id,
                    type: role.power_type,
                    status: 0
                })
                this.$Message.success('已移除成员')
                await this.refreshRoleUsers()
            } catch (err) {
                this.$Message.error(err.msg || '操作失败')
            }
        },
        // 编辑角色
        editRole(role) {
            // 目前仅用于“法定代表人”
            this.openLegalRepModal(role)
        },
        async openLegalRepModal(role) {
            this.legalRepRoleRef = role
            this.legalRepAllEmployees = []
            this.legalRepEmployees = []
            await this.loadLegalRepList()
            this.legalRepSelectedId = (role && role.members && role.members[0] && role.members[0].id) || null
            this.legalRepModalVisible = true
        },
        closeLegalRepModal() {
            this.legalRepModalVisible = false
            this.legalRepRoleRef = null
            this.legalRepAllEmployees = []
            this.legalRepEmployees = []
            this.legalRepSelectedId = null
            this.legalRepLoading = false
        },
        selectLegalRep(emp) {
            if (!emp) return
            this.legalRepSelectedId = emp.id
        },
        onLegalRepCheck(emp, checked) {
            // iView Checkbox: checked 为 true/false
            if (!emp) return
            this.legalRepSelectedId = checked ? emp.id : null
        },
        async confirmLegalRep() {
            if (!this.legalRepRoleRef) {
                this.closeLegalRepModal()
                return
            }
            if (!this.legalRepSelectedId) {
                this.$Message.warning('请选择法定代表人')
                return
            }
            const emp = (this.legalRepAllEmployees || []).find(e => e.id === this.legalRepSelectedId)
            if (!emp) {
                this.$Message.error('所选员工不存在')
                return
            }
            // 法定代表人只能一个：先取消旧的（如果有且不同），再设置新的
            const oldId = (this.legalRepRoleRef.members && this.legalRepRoleRef.members[0] && this.legalRepRoleRef.members[0].id) || null
            try {
                if (oldId && oldId !== emp.id) {
                    await upMemberPower({ member_id: oldId, type: 1, status: 0 })
                }
                await upMemberPower({ member_id: emp.id, type: 1, status: 1 })
                this.$Message.success('已更新法定代表人')
                await this.refreshRoleUsers()
                this.closeLegalRepModal()
            } catch (err) {
                this.$Message.error(err.msg || '更新失败')
            }
        },
        // 保存角色成员（接口调用）
        saveRoleMembers(role) {
            // TODO: 调用接口保存
            // saveRoleMembers({ role_id: role.id, member_ids: role.members.map(m => m.id) })
        }
    }
}
</script>

<style scoped lang="scss">
.page_wrap {
    width: 100%; 
    height: 100%;
    background: #EDF0F9;  

}

.top-card {
    background: #fff;
    border-radius: 8px;
    padding: 20px;
    padding-bottom: 300px;
}

.nav_title {
    font-size: 18px;
    font-weight: bold;
    color: #17233d;
    margin-bottom: 20px;
}

.content-box {
    .role-cards {
        display: grid;
        /* 卡片更宽一些，常见屏宽下默认 3 列 */
        grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
        gap: 24px;
    }

    .role-card {
        padding: 20px;
        border: 1px solid #e8eaec;
        border-radius: 8px;
        background: #fff;
        background: #EFF2F9;
        min-height: 180px; 

        .role-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 16px;

            .role-name {
                font-size: 16px;
                font-weight: bold;
                color: #17233d;
            }

            .edit-link {
                color: #4877E8;
                font-size: 14px;
                cursor: pointer;
                text-decoration: none;
                transition: color 0.2s;

                &:hover {
                    color: #3E5FE2;
                    text-decoration: underline;
                }
            }
        }

        .role-desc {
            margin-bottom: 16px;
            display: flex;
            align-items: flex-start;
            font-size: 14px;
            color: #515a6e;
            line-height: 1.6;

            .desc-label {
                font-weight: 500;
                color: #17233d;
                margin-right: 8px;
                white-space: nowrap;
                min-width: 72px;
            }

            .desc-content {
                color: #515a6e;
                flex: 1;
            }
        }

        .role-members {
            display: flex;
            align-items: flex-start;
            font-size: 14px;
            line-height: 1.8;

            .members-label {
                font-weight: 500;
                color: #17233d;
                margin-right: 8px;
                white-space: nowrap;
            }

            .members-list {
                flex: 1;
                display: flex;
                flex-wrap: wrap;
                gap: 8px;
                align-items: center;
                position: relative;
                min-height: 28px;
                    align-content: flex-start;

                .add-member-wrapper {
                    position: relative;
                        display: inline-flex;
                        align-items: center;
                        justify-content: center;
                        flex: 0 0 auto;
                        width: 28px;
                        height: 28px;
                        min-width: 28px;
                        min-height: 28px;
                        max-width: 28px;
                        max-height: 28px;

                    .add-member-btn {
                            width: 28px;
                            height: 28px;
                        border-radius: 50%;
                            background: #fff;
                            border: 1px solid #C8CEDA;
                            color: #545F78;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        cursor: pointer;
                        transition: all 0.3s;
                            flex: 0 0 auto;
                            box-sizing: border-box;
                            min-width: 28px;
                            min-height: 28px;

                        &:hover {
                                border-color: #4877E8;
                                color: #4877E8;
                        }

                            &.open {
                                border-color: #4877E8;
                                color: #4877E8;
                            }
                    }

                    .member-dropdown {
                        position: absolute;
                        top: 100%;
                        left: 0;
                        margin-top: 8px;
                        width: 280px;
                        height: 360px;
                        background: #fff;
                        border: 1px solid #e8eaec;
                        border-radius: 6px;
                        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
                        z-index: 1000;
                        overflow: hidden;
                        display: flex;
                        flex-direction: column;

                        &:before {
                            content: '';
                            position: absolute;
                            top: -8px;
                            left: 18px;
                            width: 0;
                            height: 0;
                            border-left: 8px solid transparent;
                            border-right: 8px solid transparent;
                            border-bottom: 8px solid #fff;
                            filter: drop-shadow(0 -1px 0 rgba(0,0,0,0.08));
                        }

                        .search-box {
                            padding: 12px;
                            border-bottom: 1px solid #e8eaec;
                            flex: 0 0 auto;
                        }

                        .employee-list {
                            flex: 1 1 auto;
                            overflow-y: auto;

                            .employee-item {
                                padding: 12px;
                                display: flex;
                                align-items: center;
                                justify-content: flex-start;
                                cursor: pointer;
                                transition: background 0.2s;
                                border-bottom: 1px solid #f5f5f5;

                                &:hover {
                                    background: #f5f7fa;
                                }

                                &:last-child {
                                    border-bottom: none;
                                }

                                .employee-name {
                                    flex: 1;
                                    color: #17233d;
                                    font-size: 14px;
                                }

                                .employee-phone {
                                    flex: 0 0 120px;
                                    text-align: right;
                                    color: #808695;
                                    font-size: 13px;
                                    margin-right: 4px;
                                }

                                .check-icon {
                                    flex: 0 0 auto;
                                    color: #4877E8;
                                    margin-left: 4px;
                                }

                                .check-slot {
                                    flex: 0 0 24px;
                                    width: 24px;
                                    display: inline-flex;
                                    align-items: center;
                                    justify-content: center;
                                }
                            }

                            .no-data {
                                padding: 20px;
                                text-align: center;
                                color: #808695;
                                font-size: 14px;
                            }
                        }
                    }
                }
            }

            .edit-link--members {
                flex: 0 0 auto;
                margin-left: 12px;
                align-self: center;
                white-space: nowrap;
            }
        }
    }
}

/* 弹窗内样式（scoped 下通过 class-name 控制） */
.legal-rep-modal {
    .legal-rep-modal__search {
        padding: 8px 4px 14px;
    }

    .legal-rep-modal__search-row {
        display: flex;
        align-items: center;
        gap: 10px;
    }

    .legal-rep-modal__list {
        max-height: 340px;
        overflow: auto;
        padding: 0 4px;
    }

    .legal-rep-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 12px 14px;
        border-radius: 6px;
        cursor: pointer;

        &:nth-child(odd) {
            background: #f5f7fa;
        }

        &:hover {
            background: #eef2ff;
        }

        .legal-rep-row__left {
            display: flex;
            align-items: center;
            gap: 10px;
            color: #17233d;
        }

        .legal-rep-row__name {
            font-size: 14px;
            color: #17233d;
        }

        .legal-rep-row__right {
            font-size: 14px;
            color: #515a6e;
        }
    }

    .legal-rep-modal__empty {
        padding: 18px 0;
        text-align: center;
        color: #999;
        font-size: 13px;
    }
}

// Tag 样式调整
/deep/ .ivu-tag {
    margin-right: 8px;
    margin-bottom: 4px;
    padding: 4px 12px;
    font-size: 14px;
    border-radius: 4px;
    background: #E3E9F5;
    border: none;
    color: #17233d;
    height: 28px;
    line-height: 20px;

    .ivu-icon-ios-close {
        margin-left: 4px;
        font-size: 16px;
        color: #808695;
        
        &:hover {
            color: #17233d;
        }
    }
}
</style>
